import React from 'react';

import FieldActionButtons from 'metadata/FieldActionButtons';
import FormInput from 'ui-library/bs/FormInput';
import FieldError from 'ui-library/FieldError';
import {Cell} from 'ui-library/simple-table/SimpleTable';
import SearchableUnitTypeDropdown from 'units/SearchableUnitTypeDropdown';

import {LibraryParameterDefinition, LibraryParameterDefinitionDataType} from '../ParameterLibraryTypes';
import {LibraryParameterDefinitionDataTypeSelector} from './LibraryParameterDefinitionDataTypeSelector';
import {LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME} from './parameterLibraryListingUtils';

// --- Name column ---

interface NameEditCellProps {
  name: string;
  onChangeName: (name: string) => void;
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  nameErrorMessage: string | null;
  className?: string;
}

export function NameEditCell({
  name,
  onChangeName,
  onKeyDown,
  nameErrorMessage,
  className,
}: NameEditCellProps): JSX.Element {
  return (
    <Cell className={className}>
      <FormInput
        type="text"
        autoFocus={true}
        value={name}
        onChange={(e) => {
          onChangeName(e.target.value);
        }}
        onKeyDown={onKeyDown}
        error={nameErrorMessage ?? undefined}
        dataTestAttrs={{component: 'ParameterLibraryListing', element: 'nameInput'}}
      />
      <FieldError message={nameErrorMessage} compact={true} />
    </Cell>
  );
}

interface NameDisplayCellProps {
  parameterDefinition: LibraryParameterDefinition;
  className: string | undefined;
}

export function NameDisplayCell({parameterDefinition, className}: NameDisplayCellProps): JSX.Element {
  return (
    <Cell className={className}>
      {parameterDefinition.name}
      {parameterDefinition.archiveData.archived ? ` (${parameterDefinition.archiveData.purpose})` : ''}
    </Cell>
  );
}

// --- Data type column ---

interface DataTypeEditCellProps {
  selectedType: LibraryParameterDefinitionDataType;
  onChangeType: (newType: LibraryParameterDefinitionDataType) => void;
  className?: string;
}

export function DataTypeEditCell({
  selectedType,
  onChangeType,
  className,
}: DataTypeEditCellProps): JSX.Element {
  return (
    <Cell className={className}>
      <LibraryParameterDefinitionDataTypeSelector
        selectedType={selectedType}
        onChangeSelection={onChangeType}
      />
    </Cell>
  );
}

interface DataTypeDisplayCellProps {
  dataType: LibraryParameterDefinitionDataType;
  className: string | undefined;
}

export function DataTypeDisplayCell({dataType, className}: DataTypeDisplayCellProps): JSX.Element {
  return (
    <Cell className={className}>{LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME[dataType]}</Cell>
  );
}

// --- Unit type column ---

interface UnitTypeEditCellProps {
  selectedId: string | null;
  onSelect: (unitType: {id: string} | null) => void;
  disabled: boolean;
  unitTypeErrorMessage: string | null;
  className?: string;
}

export function UnitTypeEditCell({
  selectedId,
  onSelect,
  disabled,
  unitTypeErrorMessage,
  className,
}: UnitTypeEditCellProps): JSX.Element {
  return (
    <Cell className={className}>
      <SearchableUnitTypeDropdown
        selectedId={selectedId}
        onSelect={onSelect}
        includeNone={false}
        disabled={disabled}
      />
      <FieldError message={unitTypeErrorMessage} compact={true} />
    </Cell>
  );
}

interface UnitTypeDisplayCellProps {
  unitTypeName: string | undefined;
  className: string | undefined;
}

export function UnitTypeDisplayCell({unitTypeName, className}: UnitTypeDisplayCellProps): JSX.Element {
  return <Cell className={className}>{unitTypeName ?? ''}</Cell>;
}

// --- Description column ---

interface DescriptionEditCellProps {
  description: string;
  onChangeDescription: (description: string) => void;
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  descriptionErrorMessage: string | null;
  descriptionCellClassName: string;
  className?: string;
}

export function DescriptionEditCell({
  description,
  onChangeDescription,
  onKeyDown,
  descriptionErrorMessage,
  descriptionCellClassName,
  className,
}: DescriptionEditCellProps): JSX.Element {
  return (
    <Cell className={className}>
      <div className={descriptionCellClassName}>
        <FormInput
          type="text"
          value={description}
          onChange={(e) => {
            onChangeDescription(e.target.value);
          }}
          onKeyDown={onKeyDown}
          error={descriptionErrorMessage ?? undefined}
          dataTestAttrs={{component: 'ParameterLibraryListing', element: 'descriptionInput'}}
        />
        <FieldError message={descriptionErrorMessage} compact={true} />
      </div>
    </Cell>
  );
}

interface DescriptionDisplayCellProps {
  description: string;
  className: string | undefined;
  descriptionCellClassName: string;
}

export function DescriptionDisplayCell({
  description,
  className,
  descriptionCellClassName,
}: DescriptionDisplayCellProps): JSX.Element {
  return (
    <Cell className={className}>
      <div className={descriptionCellClassName}>{description}</div>
    </Cell>
  );
}

// --- Action buttons column ---

interface ActionButtonsEditCellProps {
  actionButtonsClassName: string;
  isEditing: boolean;
  isSaving: boolean;
  isValid: boolean;
  onCancel: () => void;
  onComplete: () => Promise<void>;
  className?: string;
}

export function ActionButtonsEditCell({
  actionButtonsClassName,
  isEditing,
  isSaving,
  isValid,
  onCancel,
  onComplete,
  className,
}: ActionButtonsEditCellProps): JSX.Element {
  return (
    <Cell className={className}>
      <FieldActionButtons
        className={actionButtonsClassName}
        isEditing={true}
        unarchive={undefined}
        archive={undefined}
        cancel={onCancel}
        complete={onComplete}
        isCancelEnabled={isEditing && !isSaving}
        isCompleteEnabled={isEditing && isValid && !isSaving}
      />
    </Cell>
  );
}

interface ActionButtonsDisplayCellProps {
  actionButtonsClassName: string;
  parameterDefinition: LibraryParameterDefinition;
  isEditing: boolean;
  onArchive: (pd: LibraryParameterDefinition) => void;
  onUnarchive: (pd: LibraryParameterDefinition) => void;
}

export function ActionButtonsDisplayCell({
  actionButtonsClassName,
  parameterDefinition,
  isEditing,
  onArchive,
  onUnarchive,
}: ActionButtonsDisplayCellProps): JSX.Element {
  return (
    <Cell>
      <FieldActionButtons
        className={actionButtonsClassName}
        isEditing={false}
        unarchive={
          parameterDefinition.archiveData.archived
            ? () => {
                if (!isEditing) {
                  onUnarchive(parameterDefinition);
                }
              }
            : undefined
        }
        isUnarchiveEnabled={!isEditing}
        archive={
          !parameterDefinition.archiveData.archived
            ? () => {
                if (!isEditing) {
                  onArchive(parameterDefinition);
                }
              }
            : undefined
        }
        isArchiveEnabled={!isEditing}
      />
    </Cell>
  );
}
